import styled from "@emotion/styled";
import { row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import _ from "lodash";
import React from "react";

type UiStyle = "primary" | "danger";

enum ButtonState {
	PRESSED = "pressed",
	HOVER = "hover",
	DISABLED = "disabled",
	DEFAULT = "default",
}

const TERTIARY_BUTTON_COLORS: Record<UiStyle, Record<ButtonState, string>> = {
	primary: {
		[ButtonState.DISABLED]: colors.grey80,
		[ButtonState.PRESSED]: colors.primaryDark,
		[ButtonState.HOVER]: colors.primaryDark50,
		[ButtonState.DEFAULT]: colors.primary,
	},
	danger: {
		[ButtonState.DISABLED]: colors.grey80,
		[ButtonState.PRESSED]: colors.rejectionDark,
		[ButtonState.HOVER]: colors.rejectionDark,
		[ButtonState.DEFAULT]: colors.rejection,
	},
};

interface TertiaryButtonProps {
	label: string;
	onClick: (() => void) | ((event: React.MouseEvent<HTMLDivElement>) => void);
	size?: "M" | "S";
	uiStyle?: UiStyle;
	disabled?: boolean;
	loading?: boolean;
	style?: React.CSSProperties;
	icon?: string | JSX.Element | React.ReactNode;
	className?: string;
}

export const TertiaryButton: React.FC<TertiaryButtonProps> = ({
	uiStyle = "primary",
	label,
	onClick,
	disabled,
	loading,
	style,
	size = "M",
	icon,
	className,
}) => (
	<ButtonContainer
		onClick={onClick}
		uiStyle={uiStyle}
		style={{
			pointerEvents: disabled ? "none" : "auto",
			cursor: disabled ? "none" : "pointer",
			color: disabled ? TERTIARY_BUTTON_COLORS[uiStyle].disabled : undefined,
			...style,
		}}
	>
		<ButtonContent loading={loading} style={{ gap: size === "S" ? 4 : undefined }} className={className}>
			<ButtonLabel className={size === "S" ? "p-size-xs-semi-bold" : "p-size-s-bold"}>{label}</ButtonLabel>
			{isNotNil(icon) && _.isString(icon) ? <i className={icon} style={{ fontSize: size === "S" ? 9 : 13 }} /> : icon}
		</ButtonContent>
		<LoadingContainer loading={loading}>
			<i className="pi pi-spin pi-spinner" style={{ fontSize: 15 }} />
		</LoadingContainer>
	</ButtonContainer>
);

const ButtonContainer = styled.div<{ uiStyle: UiStyle }>`
	${row(undefined, undefined, "center")};

	:hover {
		color: ${({ uiStyle }) => TERTIARY_BUTTON_COLORS[uiStyle].hover};
	}

	:active {
		color: ${({ uiStyle }) => TERTIARY_BUTTON_COLORS[uiStyle].pressed};
	}

	color: ${({ uiStyle }) => TERTIARY_BUTTON_COLORS[uiStyle].default};

	position: relative;
	width: fit-content;
`;

const ButtonLabel = styled.p``;

const ButtonContent = styled.div<{ loading: boolean | undefined }>`
	${row(8, undefined, "center")};
	visibility: ${({ loading }) => (loading ? "hidden" : "visible")};
`;

const LoadingContainer = styled.div<{ loading: boolean | undefined }>`
	visibility: ${({ loading }) => (loading ? "visible" : "hidden")};
	position: absolute;
	right: 0;
	left: 0;
	display: flex;
	justify-content: center;
`;
